import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import MetricCard from './components/MetricCard';
import PerformanceChart from './components/PerformanceChart';
import RealtimeActivity from './components/RealtimeActivity';
import CampaignComparison from './components/CampaignComparison';
import MediaMonitoring from './components/MediaMonitoring';
import ReportBuilder from './components/ReportBuilder';

import Button from '../../components/ui/Button';

const AnalyticsDashboard = () => {
  const [dateRange, setDateRange] = useState('30d');

  // Mock data for key metrics
  const keyMetrics = [
    {
      title: 'Email Open Rate',
      value: '68.5%',
      change: '+12.3%',
      changeType: 'positive',
      icon: 'Mail',
      trend: true
    },
    {
      title: 'Click-through Rate',
      value: '24.7%',
      change: '+8.1%',
      changeType: 'positive',
      icon: 'MousePointer',
      trend: true
    },
    {
      title: 'Press Kit Downloads',
      value: '1,247',
      change: '+156',
      changeType: 'positive',
      icon: 'Download',
      trend: true
    },
    {
      title: 'Media Mentions',
      value: '89',
      change: '-5.2%',
      changeType: 'negative',
      icon: 'MessageSquare',
      trend: false
    },
    {
      title: 'Contact Engagement',
      value: '92.1%',
      change: '+3.4%',
      changeType: 'positive',
      icon: 'Users',
      trend: true
    },
    {
      title: 'Bounce Rate',
      value: '2.8%',
      change: '-1.2%',
      changeType: 'positive',
      icon: 'AlertTriangle',
      trend: false
    }
  ];

  // Mock data for email performance chart
  const emailPerformanceData = [
    { name: 'Oct 25', value: 65 },
    { name: 'Oct 26', value: 72 },
    { name: 'Oct 27', value: 68 },
    { name: 'Oct 28', value: 78 },
    { name: 'Oct 29', value: 85 },
    { name: 'Oct 30', value: 82 },
    { name: 'Oct 31', value: 89 }
  ];

  // Mock data for engagement trends
  const engagementData = [
    { name: 'Week 1', value: 1240 },
    { name: 'Week 2', value: 1580 },
    { name: 'Week 3', value: 1320 },
    { name: 'Week 4', value: 1890 },
    { name: 'Week 5', value: 2100 }
  ];

  const dateRanges = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
    { value: '1y', label: '1 Year' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-semibold text-foreground">Analytics Dashboard</h1>
              <p className="text-muted-foreground mt-2">
                Comprehensive performance tracking for PR campaigns and media engagement
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                {dateRanges?.map((range) => (
                  <Button
                    key={range?.value}
                    variant={dateRange === range?.value ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setDateRange(range?.value)}
                  >
                    {range?.label}
                  </Button>
                ))}
              </div>
              
              <Button variant="outline" iconName="RefreshCw" iconPosition="left">
                Refresh Data
              </Button>
              
              <Button variant="default" iconName="Download" iconPosition="left">
                Export Report
              </Button>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {keyMetrics?.map((metric, index) => (
              <MetricCard
                key={index}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                trend={metric?.trend}
              />
            ))}
          </div>

          {/* Performance Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <PerformanceChart
              title="Email Performance Trends"
              data={emailPerformanceData}
              type="line"
              height={320}
            />
            
            <PerformanceChart
              title="Weekly Engagement Volume"
              data={engagementData}
              type="bar"
              height={320}
            />
          </div>

          {/* Campaign Comparison */}
          <div className="mb-8">
            <CampaignComparison />
          </div>

          {/* Real-time Activity and Media Monitoring */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
            <div className="xl:col-span-1">
              <RealtimeActivity />
            </div>
            
            <div className="xl:col-span-2">
              <MediaMonitoring />
            </div>
          </div>

          {/* Report Builder */}
          <div className="mb-8">
            <ReportBuilder />
          </div>

          {/* Quick Actions */}
          <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
            <h3 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Button
                variant="outline"
                className="h-auto p-4 flex flex-col items-center space-y-2"
                iconName="AlertCircle"
              >
                <span className="text-sm font-medium">Set Up Alerts</span>
                <span className="text-xs text-muted-foreground text-center">
                  Configure notifications for key metrics
                </span>
              </Button>
              
              <Button
                variant="outline"
                className="h-auto p-4 flex flex-col items-center space-y-2"
                iconName="Target"
              >
                <span className="text-sm font-medium">Campaign Goals</span>
                <span className="text-xs text-muted-foreground text-center">
                  Set and track performance targets
                </span>
              </Button>
              
              <Button
                variant="outline"
                className="h-auto p-4 flex flex-col items-center space-y-2"
                iconName="Calendar"
              >
                <span className="text-sm font-medium">Schedule Reports</span>
                <span className="text-xs text-muted-foreground text-center">
                  Automate report delivery
                </span>
              </Button>
              
              <Button
                variant="outline"
                className="h-auto p-4 flex flex-col items-center space-y-2"
                iconName="Share2"
              >
                <span className="text-sm font-medium">Share Dashboard</span>
                <span className="text-xs text-muted-foreground text-center">
                  Generate shareable links
                </span>
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;